'use client'

import { persistWorkspaceId } from '@/lib/tenant/workspace-id-storage';
import { useState, useEffect, useRef, Suspense, useSyncExternalStore, type FormEvent } from 'react'
import { useRouter, useSearchParams } from 'next/navigation'
import Link from 'next/link'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@/lib/zod-resolver'
import { z } from 'zod/v4'
import { Shield, Eye, EyeOff, Loader2, Mail, KeyRound, Fingerprint, Users, ArrowRight, Building2 } from 'lucide-react'
import { startAuthentication } from '@simplewebauthn/browser'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Badge } from '@/components/ui/badge'
import { cn } from '@/lib/utils'
import {
  getLastUsedMethod,
  recordLastUsedMethod,
  stageSsoAttempt,
  type LoginMethod,
} from '@/lib/auth/last-used-method'
import { toast } from 'sonner'
import { authApi, type LoginResponse } from '@/lib/api/auth'
import { useAuthStore } from '@/stores/auth-store'
import { useTranslation } from '@/lib/i18n'
import { LanguageSwitcher } from '@/components/language-switcher'
import { BrandedLoader } from '@/components/branded-loader'
import { resolveSafeRedirect } from '@/lib/auth/safe-redirect'
import { holdReturnPath } from '@/lib/auth/return-after-sign-in'
import { codeDigits } from '@/lib/auth/one-time-code'
import { SessionEndedNotice } from '@/components/status/session-ended-notice'
import { landedFromLapsedSession } from '@/lib/status/session-ended'
import {
  adminRefusalKey,
  adminSsoApi,
  errorCodeOf,
  goToIdentityProvider,
  isOpenableIdpUrl,
  isSsoRequired,
  isWrongCompanyCode,
  sha256Hex,
  sharedAppStartUrl,
  ssoRequiredNoticeKey,
  startSsoBinding,
} from '@/components/identity/sso/admin-sign-in-api'
import { ROUTES } from '@/lib/routes'
import { isOrgMethod } from '@/lib/sign-in/methods'
import {
  SignInCompanyBrand,
  SignInCompanyFooter,
  SignInCompanyNote,
} from '@/components/auth/sign-in-company-card'
import { SignInRedirectButton } from '@/components/auth/sign-in-redirect-button'
import { SignInUnavailable } from '@/components/auth/sign-in-unavailable'
import { SignInWrongCompany } from '@/components/auth/sign-in-wrong-company'
import {
  emailDomain,
  signInOptionsForEmail,
  signInViewOf,
  useHostSignInOptions,
  type FormTab,
  type RedirectButton,
  type SignInView,
} from './_lib/sign-in-view'

// ── Validation Schemas ──────────────────────────────────────

const loginSchema = z.object({
  email: z.email('Please enter a valid email address'),
  password: z.string().min(1, 'Password is required'),
})

const otpEmailSchema = z.object({
  email: z.email('Please enter a valid email address'),
})

const otpVerifySchema = z.object({
  email: z.email('Please enter a valid email address'),
  // Folded, then counted: `.length(6)` alone passed '١٢٣٤٥٦' through as typed,
  // and the API refused it as 12 bytes against 6.
  otp: z.string().transform(codeDigits).pipe(z.string().length(6, 'OTP must be 6 digits')),
})

type LoginFormData = z.infer<typeof loginSchema>
type OtpEmailData = z.infer<typeof otpEmailSchema>
type OtpVerifyData = z.infer<typeof otpVerifySchema>

// ── Safe redirect helper ────────────────────────────────────

/**
 * Resolve the post-login redirect target from the `next` query param, with
 * open-redirect protection. The producer is the accept-invite page, which
 * sends `?next=<encoded internal path>` so the user lands back on
 * `/accept-invite?token=…` after authenticating (where its sessionStorage
 * pending-token logic finishes the accept).
 *
 * Only SAFE INTERNAL paths are honored: the value must start with a single
 * `/` and must NOT begin with `//` or `/\` (protocol-relative / backslash
 * tricks that browsers treat as absolute external URLs — the classic
 * open-redirect vector). Anything else (absolute `http(s)://`, scheme-relative,
 * empty, or malformed) falls back to the default `/recipients`.
 */
function resolveSafeNext(raw: string | null): string {
  // One rule for every post-auth redirect — the OAuth callback shares it.
  return resolveSafeRedirect(raw, '/dashboard')
}

/**
 * Leave for HumanFirewall's shared Microsoft or Google sign-in, telling the backend which host to
 * send the browser BACK to.
 *
 * This one app is served on several product hosts (dash / play / portal / educate) and, since
 * branded sign-in (3 Nov 2026), on a company's own address. Without `redirectTo`, the callback
 * always bounced to APP_BASE_URL — so an employee signing in on the play/portal host was dumped on
 * the ADMIN console. We pass the origin we actually started on; the backend allowlists it
 * (`AUTH_ALLOWED_REDIRECT_ORIGINS`, or a live address of the person's own company) and falls back
 * to APP_BASE_URL if it is not trusted, so this can never become an open redirect.
 *
 * `bindingHash` (decision 8): the SHA-256 of a random value kept in this tab. The API stores the
 * finished sign-in behind a one-time code and sends the browser to /auth/sso/complete?handoff=,
 * which redeems it with the value itself, so the session never travels in the address bar (it used
 * to come back as /auth/callback?tokens=) and a code that leaked works in no other browser.
 */
async function startSharedApp(provider: 'microsoft' | 'google'): Promise<void> {
  const binding = startSsoBinding()
  const bindingHash = await sha256Hex(binding)
  goToIdentityProvider(sharedAppStartUrl(provider, window.location.origin, bindingHash))
}

/** The sentence that says which method a company refused (A2), by the form that was refused. */
type RefusedMethod = 'password' | 'email_code' | 'passkey'
const REFUSED_KEYS: Readonly<Record<RefusedMethod, string>> = {
  password: 'signInPage.refused.password',
  email_code: 'signInPage.refused.emailCode',
  passkey: 'signInPage.refused.passkey',
}

/** A company address whose only way in is one provider: "Microsoft's sign-in page opens next" (A4). */
const REDIRECT_ONLY_KEYS: Readonly<Record<RedirectButton['provider'], string>> = {
  microsoft: 'signInPage.company.redirectOnlyMicrosoft',
  google: 'signInPage.company.redirectOnlyGoogle',
  saml: 'signInPage.company.redirectOnlyOther',
  oidc: 'signInPage.company.redirectOnlyOther',
}

/** Today's tabs: every company that changed nothing gets exactly these. */
const ALL_TABS: readonly FormTab[] = ['password', 'otp', 'passkey']

// ── Component ───────────────────────────────────────────────

function LoginPageInner() {
  const router = useRouter()
  const searchParams = useSearchParams()
  const { t } = useTranslation()
  const [showPassword, setShowPassword] = useState(false)
  const [otpSent, setOtpSent] = useState(false)
  const [otpEmail, setOtpEmail] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)
  // SSO loading is PER-PROVIDER, not shared: clicking Google must spin only the
  // Google button, never Microsoft (they used to share `isSubmitting`, so one
  // click lit both). A full-page redirect follows, so we keep the flag set —
  // the spinner stays until the browser navigates away.
  const [ssoLoading, setSsoLoading] = useState<'microsoft' | 'google' | null>(null)

  // Sign in with the organisation's single sign-on (identity integrations wave 2): e-mail -> the identity
  // provider. Opened by the button, by a refused attempt (`?sso_error=<code>` from the API, or a password /
  // e-mail code / passkey attempt the organisation refuses with sso_required), and the refusal is worded here.
  const ssoErrorParam = searchParams.get('sso_error')
  const [orgSsoOpen, setOrgSsoOpen] = useState<boolean>(Boolean(ssoErrorParam))
  const [orgSsoEmail, setOrgSsoEmail] = useState('')
  const [orgSsoSubmitting, setOrgSsoSubmitting] = useState(false)
  const [orgSsoNoticeKey, setOrgSsoNoticeKey] = useState<string | null>(
    ssoErrorParam && !isWrongCompanyCode(ssoErrorParam) ? adminRefusalKey(ssoErrorParam) : null,
  )
  const [orgSsoEmailInvalid, setOrgSsoEmailInvalid] = useState(false)

  // Branded sign-in (3 Nov 2026): which methods THIS address offers. A company address (csb.humanfirewall.ai, or the
  // company's own domain) answers with the company's logo and only its methods, and nothing is drawn until the answer
  // is in, so the page never flashes a method the company switched off. dash, and an answer that never came, is
  // today's page.
  const host = useHostSignInOptions('admins')
  const addressView = signInViewOf(host.options)
  // dash only: the typed work e-mail's DOMAIN may belong to a company that changed how it signs in (A1). One lookup per
  // domain; the answer is the same for every address of it.
  const [emailLookup, setEmailLookup] = useState<{ email: string; view: SignInView } | null>(null)
  const lookedUpDomain = useRef('')
  const lookupSeq = useRef(0)
  const emailView = addressView ? null : (emailLookup?.view ?? null)
  // The view the card is drawn from; null = today's page.
  const view = addressView ?? emailView
  const tabs = view ? view.tabs : ALL_TABS
  const [activeTab, setActiveTab] = useState<FormTab>('password')
  const tabValue = tabs.includes(activeTab) ? activeTab : tabs[0]
  // A method the company refused anyway, typed before the card changed (A2), and the company's way in.
  const [refusal, setRefusal] = useState<{ method: RefusedMethod; view: SignInView | null; email: string | null } | null>(
    null,
  )
  // An account of another organisation on this company's address (A6). From the provider's round trip the account is
  // not known (no address in a URL); from a password or a code it is the one typed here.
  const [wrongCompany, setWrongCompany] = useState<{ email: string | null } | null>(
    isWrongCompanyCode(ssoErrorParam) ? { email: null } : null,
  )
  // Which provider button is on its way out (one at a time; the spinner stays until the browser leaves).
  const [redirecting, setRedirecting] = useState<string | null>(null)

  // Which method this browser signed in with last, for the "Last used" badge.
  //
  // useSyncExternalStore rather than useEffect+setState: it gives React a
  // SERVER snapshot (null — localStorage does not exist there) and a CLIENT
  // snapshot, so the markup hydrates without a mismatch and without the
  // cascading re-render an effect would cause. The value cannot change while
  // this page is mounted — it is only ever written on the way OUT of it — so
  // the subscribe callback has nothing to listen to. getSnapshot returns a
  // string or null, which React compares with Object.is, so it stays stable.
  const lastUsed = useSyncExternalStore<LoginMethod | null>(
    NO_STORE_SUBSCRIBE,
    getLastUsedMethod,
    () => null,
  )
  // Auth-redirect gate. While true, render <BrandedLoader/> instead of the
  // login form so authed users never flash the unauthed UI on direct hits
  // to /login. Closes the "login page shows even when logged in" tester
  // report (2026-05-08). The companion SSR redirect at app/page.tsx covers
  // the / → /login bounce; this gate covers direct /login navigations.
  const [authGateChecking, setAuthGateChecking] = useState(true)

  // Email + Password form
  const loginForm = useForm<LoginFormData>({
    resolver: zodResolver(loginSchema),
    defaultValues: { email: '', password: '' },
  })

  // OTP email form
  const otpEmailForm = useForm<OtpEmailData>({
    resolver: zodResolver(otpEmailSchema),
    defaultValues: { email: '' },
  })

  // OTP verify form
  const otpVerifyForm = useForm<OtpVerifyData>({
    resolver: zodResolver(otpVerifySchema),
    defaultValues: { email: '', otp: '' },
  })

  // Redirect if already authenticated. While the cookie check runs (and
  // while a positive-match redirect is in flight) we render <BrandedLoader/>
  // below instead of the login form so the unauthed UI never flashes for
  // logged-in users.
  useEffect(() => {
    // A6 from the proxy (?sso_error=company_address_wrong_company): the session in this browser is another company's.
    // The proxy cleared its cookie, but the stored session re-arms it on load, so sign it out HERE first; redirecting
    // it "on" would send it back to the proxy, which sends it here again.
    if (searchParams.get('sso_error') === 'company_address_wrong_company') {
      useAuthStore.getState().logout('stay-on-page')
      setAuthGateChecking(false)
      return
    }
    const token = document.cookie.includes('hf-app:access-token')
    if (token) {
      // Already authenticated → honor a safe `next` (e.g. the accept-invite
      // round-trip) instead of always bouncing to /recipients.
      router.push(resolveSafeNext(searchParams.get('next')))
      // Keep the gate `true` so the loader stays on screen until the
      // navigation completes (replacing the page entirely).
      return
    }
    setAuthGateChecking(false)
  }, [router, searchParams])

  // ── Handlers ────────────────────────────────────────────

  /**
   * A login response that is a 2FA CHALLENGE, not a session.
   *
   * Every one of the four sign-in paths on this page can now answer with one
   * (password since RC-E; SSO/OTP/passkey already could). Handing such a
   * response to handleLoginSuccess wrote `accessToken: ''` into localStorage
   * and navigated to the dashboard with no credential — the user saw a broken
   * app instead of a code prompt. Returns true when it routed.
   */
  const routeIfSecondFactorRequired = (response: LoginResponse): boolean => {
    if (!response?.requiresTwoFactor || !response.tempToken) return false
    if (typeof window !== 'undefined') {
      // Same keys /verify-2fa reads and the SSO callback writes.
      sessionStorage.setItem('hf-2fa-email', response.email ?? '')
      sessionStorage.setItem('hf-2fa-temp-token', response.tempToken)
      // /verify-2fa finishes this sign-in, so it takes `next` from here.
      holdReturnPath(searchParams.get('next'))
    }
    router.push('/verify-2fa')
    return true
  }

  const handleLoginSuccess = async (response: LoginResponse) => {
    // Backend returns { user, tokens: { accessToken, refreshToken } }
    // OTP flow returns { accessToken, refreshToken } at top level (no user/tokens wrapper)
    // Password flow returns { user, tokens: { accessToken, refreshToken } }
    const user = (response.user ?? {}) as unknown as Record<string, string>;
    const accessToken = response.tokens?.accessToken ?? response.accessToken ?? '';
    const refreshToken = response.tokens?.refreshToken ?? response.refreshToken ?? '';

    // A token-less "success" is never a success. This used to write '' into
    // localStorage and carry on, which is what made a 2FA challenge look like a
    // broken dashboard rather than a missing step.
    if (!accessToken) {
      throw new Error('Sign-in did not return an access token.')
    }

    // Store tokens immediately so we can make authenticated requests
    if (typeof window !== 'undefined') {
      localStorage.setItem('hf-app:access-token', accessToken);
      localStorage.setItem('hf-app:refresh-token', refreshToken);
    }

    // Fetch workspaces to get the workspace ID (not returned in login)
    let workspaceId: string | undefined;
    try {
      const API_URL = process.env.NEXT_PUBLIC_API_URL || 'http://localhost:4001';
      const wsResponse = await fetch(`${API_URL}/v1/workspaces/my`, {
        headers: { 'Authorization': `Bearer ${accessToken}` },
      });
      if (wsResponse.ok) {
        const workspaces = await wsResponse.json();
        const wsList = Array.isArray(workspaces) ? workspaces : workspaces.data || [];
        if (wsList.length > 0) {
          workspaceId = wsList[0].id;
          // BOTH channels: the server side reads the cookie, and only the
          // cookie. A localStorage-only write here left every route handler on
          // whatever workspace the JWT claim happened to name.
          persistWorkspaceId(workspaceId!);
        }
      }
    } catch {
      // Workspace fetch failed — proceed without it
    }

    const loginData = {
      user: user as unknown as Parameters<ReturnType<typeof useAuthStore.getState>['login']>[0]['user'],
      accessToken,
      refreshToken,
      workspaceId,
    };
    useAuthStore.getState().login(loginData);
    toast.success(t('auth.loginSuccess'), {
      description: t('auth.welcomeBackName', { name: user?.firstName || user?.email || 'there' }),
    });
    // Accounts provisioned with a one-time temp password must set a real
    // password before doing anything else (takes precedence over `next`).
    // /auth/set-password finishes this sign-in, so it takes `next` from here.
    if (user?.mustChangePassword) {
      holdReturnPath(searchParams.get('next'));
      router.push('/auth/set-password');
      return;
    }
    // Honor a safe `next` (e.g. accept-invite → login → return-and-accept)
    // instead of always landing on /recipients. Open-redirect-guarded.
    router.push(resolveSafeNext(searchParams.get('next')));
  }

  /**
   * The organisation requires single sign-on: say so (or, release 7, that it is PAUSED and who can still sign in), and
   * open the SSO form with the address already typed.
   */
  const showSsoRequired = (email: string | undefined, error: unknown) => {
    setOrgSsoNoticeKey(ssoRequiredNoticeKey(error))
    if (email) setOrgSsoEmail(email)
    setOrgSsoOpen(true)
  }

  /** E-mail -> POST /auth/sso/initiate -> the identity provider. One answer for every address it cannot serve. */
  const onOrgSsoSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    const email = orgSsoEmail.trim()
    if (!z.email().safeParse(email).success) {
      setOrgSsoEmailInvalid(true)
      return
    }
    setOrgSsoEmailInvalid(false)
    setOrgSsoSubmitting(true)
    try {
      // This tab's binding: /auth/sso/complete sends it back, so the code works only here.
      const binding = startSsoBinding()
      const { redirectUrl } = await adminSsoApi.initiate(email, binding)
      if (!isOpenableIdpUrl(redirectUrl)) throw new Error('not an identity provider address')
      // /auth/sso/complete (or /verify-2fa) finishes this sign-in, so it takes `next` from here.
      holdReturnPath(searchParams.get('next'))
      // The spinner stays until the browser has left for the identity provider.
      goToIdentityProvider(redirectUrl)
    } catch (error) {
      setOrgSsoNoticeKey(adminRefusalKey(errorCodeOf(error)))
      setOrgSsoSubmitting(false)
    }
  }

  /**
   * dash, once a work e-mail is entered (A1): ask which methods its DOMAIN's company offers. Never on a company
   * address (the address decides), never for a half-typed address, once per domain, and only the latest answer counts.
   */
  const lookUpEmail = (raw: string) => {
    if (addressView || !host.checked) return
    const email = raw.trim()
    if (!z.email().safeParse(email).success) return
    const domain = emailDomain(email)
    if (domain === lookedUpDomain.current) return
    lookedUpDomain.current = domain
    const seq = ++lookupSeq.current
    void signInOptionsForEmail('admins', email).then((options) => {
      if (seq !== lookupSeq.current) return
      const found = signInViewOf(options)
      setEmailLookup(found ? { email, view: found } : null)
    })
  }

  /** "Use a different e-mail": back to today's page, the address cleared. */
  const changeEmail = () => {
    lookupSeq.current += 1
    lookedUpDomain.current = ''
    setEmailLookup(null)
    setRefusal(null)
    loginForm.setValue('email', '')
    otpEmailForm.setValue('email', '')
  }

  /**
   * The company refused this method (sign_in_method_off, A2): say so in the company's words and show its way in. On
   * dash the company is the one the e-mail's domain names; with no company card for it, the plain sentence.
   */
  const showRefusal = async (method: RefusedMethod, email: string | null) => {
    let refusedView: SignInView | null = addressView
    if (!refusedView && email) refusedView = signInViewOf(await signInOptionsForEmail('admins', email))
    setRefusal({ method, view: refusedView, email })
  }

  /** Any refusal of the four form sign-ins that branded sign-in words itself; true when it did. */
  const handledByBrandedSignIn = async (error: unknown, method: RefusedMethod, email: string | null): Promise<boolean> => {
    const code = errorCodeOf(error)
    if (isWrongCompanyCode(code)) {
      setWrongCompany({ email })
      return true
    }
    if (code === 'sign_in_method_off') {
      await showRefusal(method, email)
      return true
    }
    return false
  }

  /** Leave for a provider: the company's own app (initiate), or HumanFirewall's shared one (binding hash). */
  const startRedirect = async (button: RedirectButton, email: string | null) => {
    if (redirecting) return
    setRedirecting(button.method)
    // /auth/sso/complete (or /verify-2fa) finishes this sign-in, so it takes `next` from here.
    holdReturnPath(searchParams.get('next'))
    try {
      if (isOrgMethod(button.method)) {
        const binding = startSsoBinding()
        // On a company address the address names the company; on dash the e-mail does.
        const { redirectUrl } = await adminSsoApi.initiate(addressView ? null : email, binding, button.method)
        if (!isOpenableIdpUrl(redirectUrl)) throw new Error('not an identity provider address')
        goToIdentityProvider(redirectUrl)
      } else {
        const provider = button.method === 'hf_google' ? 'google' : 'microsoft'
        // Staged, not recorded: /auth/sso/complete promotes it only on a confirmed return.
        stageSsoAttempt(provider)
        await startSharedApp(provider)
      }
    } catch (error) {
      setOrgSsoNoticeKey(adminRefusalKey(errorCodeOf(error)))
      setRedirecting(null)
    }
  }

  const onLoginSubmit = async (data: LoginFormData) => {
    setIsSubmitting(true)
    try {
      const response = await authApi.login(data.email, data.password)
      // A 2FA-enrolled account gets a challenge, not a session (RC-E).
      if (routeIfSecondFactorRequired(response)) return
      // Confirmed sign-in — safe to remember. Recorded per method rather than
      // inside handleLoginSuccess, which all three paths share.
      recordLastUsedMethod('password')
      await handleLoginSuccess(response)
    } catch (error) {
      if (isSsoRequired(error)) {
        showSsoRequired(data.email, error)
        return
      }
      if (await handledByBrandedSignIn(error, 'password', data.email)) return
      const message = error instanceof Error ? error.message : 'Login failed. Please check your credentials.'
      toast.error(t('auth.loginFailed'), { description: message })
    } finally {
      setIsSubmitting(false)
    }
  }

  const onSendOtp = async (data: OtpEmailData) => {
    setIsSubmitting(true)
    try {
      await authApi.sendOtp(data.email)
      setOtpEmail(data.email)
      setOtpSent(true)
      otpVerifyForm.setValue('email', data.email)
      toast.success(t('auth.otpSent'), {
        description: t('auth.otpSentDesc', { email: data.email }),
      })
    } catch (error) {
      // Refused by the address or the e-mail's domain before any code is sent (A2).
      if (await handledByBrandedSignIn(error, 'email_code', data.email)) return
      const message = error instanceof Error ? error.message : t('auth.failedSendOtp')
      toast.error(t('auth.failedSendOtp'), { description: message })
    } finally {
      setIsSubmitting(false)
    }
  }

  const onVerifyOtp = async (data: OtpVerifyData) => {
    setIsSubmitting(true)
    try {
      const response = await authApi.verifyOtp(data.email, data.otp)
      if (routeIfSecondFactorRequired(response)) return
      // Confirmed sign-in — safe to remember. Recorded per method rather than
      // inside handleLoginSuccess, which all three paths share.
      recordLastUsedMethod('otp')
      await handleLoginSuccess(response)
    } catch (error) {
      if (isSsoRequired(error)) {
        showSsoRequired(data.email, error)
        return
      }
      if (errorCodeOf(error) === 'sign_in_method_off') setOtpSent(false)
      if (await handledByBrandedSignIn(error, 'email_code', data.email)) return
      const message = error instanceof Error ? error.message : 'Invalid or expired OTP'
      toast.error(t('auth.verificationFailed'), { description: message })
    } finally {
      setIsSubmitting(false)
    }
  }

  const onPasskeyLogin = async () => {
    setIsSubmitting(true)
    try {
      // 1. Get authentication options from backend
      const { options } = await authApi.passkeyLoginOptions()
      // 2. Trigger browser biometric prompt
      const authResponse = await startAuthentication({ optionsJSON: options as unknown as Parameters<typeof startAuthentication>[0]['optionsJSON'] })
      // 3. Send response to backend for verification + JWT
      const response = await authApi.passkeyLoginVerify(authResponse as unknown as Parameters<typeof authApi.passkeyLoginVerify>[0])
      // Passkey login is usernameless, so the API had to start returning
      // `email` on its challenge for /verify-2fa to be reachable at all.
      if (routeIfSecondFactorRequired(response)) return
      // Confirmed sign-in — safe to remember. Recorded per method rather than
      // inside handleLoginSuccess, which all three paths share.
      recordLastUsedMethod('passkey')
      await handleLoginSuccess(response)
    } catch (error) {
      if (isSsoRequired(error)) {
        showSsoRequired(undefined, error)
        return
      }
      // Usernameless: the account is not known here, so the refusal names none.
      if (await handledByBrandedSignIn(error, 'passkey', null)) return
      const message = error instanceof Error ? error.message : t('auth.passkeyFailed')
      toast.error(t('auth.passkeyFailed'), { description: message })
    } finally {
      setIsSubmitting(false)
    }
  }

  // ── Render ──────────────────────────────────────────────

  // Auth gate: while we're checking the cookie (and during a logged-in
  // redirect) show the branded loader so the form never flashes for
  // already-authenticated users. See the useEffect above. It also waits for
  // this address's methods (`host`), so a company address never flashes a
  // method its company switched off.
  if (authGateChecking || !host.checked) {
    return <BrandedLoader />
  }

  /** What a provider button is called in a sentence: the company's label, else the provider's name. */
  const appName = (button: Pick<RedirectButton, 'label' | 'provider'>): string =>
    button.label ??
    (button.provider === 'microsoft'
      ? t('auth.microsoft')
      : button.provider === 'google'
        ? t('auth.google')
        : t('signInPage.providers.sso'))

  /** The card's line under "Welcome back": today's, or the company's (A3 to A5); none when only an outage is left (A7). */
  const cardDescription = (() => {
    if (!addressView) return t('auth.signInDesc')
    const company = addressView.company.name
    if (addressView.tabs.length > 0) return t('signInPage.company.desc', { company })
    if (addressView.redirects.length === 1) return t(REDIRECT_ONLY_KEYS[addressView.redirects[0].provider], { company })
    if (addressView.redirects.length > 1) return t('signInPage.company.redirectChoice', { company })
    return null
  })()

  const passkeyOffered = tabs.includes('passkey') || Boolean(view?.passkeyAlone)
  // The two e-mail fields also ask which methods the address's domain has (A1) and drop a refusal once edited (A2).
  const loginEmailField = loginForm.register('email')
  const otpEmailField = otpEmailForm.register('email')

  /** A2: the method the company refused, in its words, and the company's way in. */
  const renderRefusal = (method: RefusedMethod) => {
    if (refusal?.method !== method) return null
    const refused = refusal.view
    const first = refused?.redirects[0]
    return (
      <div className="space-y-3">
        <div
          role="alert"
          data-testid="sign-in-refused"
          className="rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-destructive"
        >
          {refused
            ? `${t(REFUSED_KEYS[method], { company: refused.company.name })} ${
                first ? t('signInPage.refused.useApp', { app: appName(first) }) : t('signInPage.refused.useAnother')
              }`
            : t('signInErrors.methodOff')}
        </div>
        {refused ? (
          <>
            {refused.redirects.map((button, index) => (
              <SignInRedirectButton
                key={button.method}
                button={button}
                wording="continueWith"
                primary={index === 0}
                loading={redirecting === button.method}
                disabled={redirecting !== null}
                onClick={() => void startRedirect(button, refusal.email)}
              />
            ))}
            {method !== 'email_code' && refused.tabs.includes('otp') ? (
              <Button type="button" variant="outline" className="h-11 w-full sm:h-10" onClick={() => switchTab('otp')}>
                {t('signInPage.buttons.useEmailCode')}
              </Button>
            ) : null}
            {method !== 'password' && refused.tabs.includes('password') ? (
              <Button type="button" variant="outline" className="h-11 w-full sm:h-10" onClick={() => switchTab('password')}>
                {t('signInPage.buttons.usePassword')}
              </Button>
            ) : null}
          </>
        ) : null}
      </div>
    )
  }

  /** A2's other way in: the tab the company keeps, the address carried over. */
  const switchTab = (tab: FormTab) => {
    const email = refusal?.email
    setRefusal(null)
    if (email) {
      loginForm.setValue('email', email)
      otpEmailForm.setValue('email', email)
    }
    setActiveTab(tab)
  }

  /** A company's provider buttons in a row beside its form (A3, A5). */
  const providerRow = (buttons: RedirectButton[]) => (
    <div className={cn('grid grid-cols-1 gap-3', buttons.length > 1 && 'sm:grid-cols-2')}>
      {buttons.map((button) => (
        <SignInRedirectButton
          key={button.method}
          button={button}
          wording="short"
          loading={redirecting === button.method}
          disabled={redirecting !== null}
          onClick={() => void startRedirect(button, emailLookup?.email ?? null)}
        >
          {(button.method === 'hf_microsoft' && lastUsed === 'microsoft') ||
          (button.method === 'hf_google' && lastUsed === 'google') ? (
            <LastUsedBadge />
          ) : null}
        </SignInRedirectButton>
      ))}
    </div>
  )

  return (
    // Its own scroller: html and body are overflow:hidden for the app shell
    // (globals.css), so a page outside the shell scrolls only in itself. The
    // card is centred with m-auto, not items-center, so a form taller than
    // the window starts at the top and scrolls instead of being cut off.
    <div
      data-testid="login-page-scroller"
      className="relative h-dvh overflow-y-auto bg-background flex flex-col p-4 sm:p-6"
    >
      {/* Top header cluster — Employee Portal entry + Language Switcher.
          Why this lives here: the admin login is the wrong door for an
          employee; without a visible "go to employee portal" affordance
          they bounce off and call IT. Theme tokens (bg-card, primary,
          muted-foreground) come from the same shadcn token set the portal
          login uses — they render identically in both light + dark mode.
          Why /portal/login: the portal login page handles its own
          ?ws=<workspaceId> query-param + sessionStorage workspace
          resolution chain (see app/(portal)/layout.tsx), so the admin
          login doesn't need to pre-resolve a workspace. */}
      <div className="absolute top-3 end-3 sm:top-4 sm:end-4 flex items-center gap-2">
        <Link
          href="/portal/login"
          className="inline-flex items-center gap-2 rounded-md border border-border bg-card hover:bg-muted text-sm font-medium px-3 py-1.5 text-foreground transition-colors"
          aria-label={t('auth.goToEmployeePortal')}
          data-testid="admin-login-go-to-portal"
        >
          <Users className="h-4 w-4 text-primary" aria-hidden="true" />
          <span className="hidden sm:inline">{t('auth.employeePortal')}</span>
          <ArrowRight className="h-3.5 w-3.5 opacity-60 rtl-flip" aria-hidden="true" />
        </Link>
        <LanguageSwitcher />
      </div>
      <div data-testid="login-page-content" className="w-full max-w-md m-auto pt-10 sm:pt-0">
        {/* Brand Header — or, when a lapsed session sent the person here from a
            page (`next`), the 401 picture saying so. Sign-in below is the same. */}
        {landedFromLapsedSession(searchParams.get('next')) ? (
          <SessionEndedNotice className="mb-6 sm:mb-8" />
        ) : addressView ? (
          // A company address: its logo and name where HumanFirewall's stand (A3 to A7).
          <SignInCompanyBrand company={addressView.company} />
        ) : (
          <div className="flex flex-col items-center mb-6 sm:mb-8">
            <div className="flex items-center justify-center h-12 w-12 sm:h-14 sm:w-14 rounded-xl bg-primary/10 text-primary mb-3 sm:mb-4">
              <Shield className="h-7 w-7 sm:h-8 sm:w-8" />
            </div>
            <h1 className="text-xl sm:text-2xl font-bold tracking-tight">{t('auth.brand')}</h1>
            <p className="text-xs sm:text-sm text-muted-foreground mt-1 text-center px-2">
              {t('auth.tagline')}
            </p>
          </div>
        )}

        {wrongCompany ? (
          // A6: an account of another organisation on this company's address. Nothing was opened.
          <SignInWrongCompany
            company={addressView ? addressView.company : null}
            email={wrongCompany.email}
            platformUrl={host.options?.platformUrl ?? null}
            onDifferentAccount={() => {
              setWrongCompany(null)
              loginForm.setValue('password', '')
            }}
          />
        ) : (
        <>
        {/* Login Card */}
        <Card>
          <CardHeader className="text-center pb-4">
            <CardTitle className="text-xl">{t('auth.welcomeBack')}</CardTitle>
            {cardDescription ? <CardDescription>{cardDescription}</CardDescription> : null}
          </CardHeader>
          <CardContent>
            {/* The mid-session cascade lands HERE, on a page whose title reads
                "Welcome back" — which is precisely what the 2026-09-17 report
                described: correct credentials, a welcome, and then the login
                form again with nothing explaining why. The reason arrives as a
                query param because a toast cannot survive the hard navigation
                in lib/api/client.ts. Inline and persistent, not a toast. */}
            {orgSsoNoticeKey ? (
              <div
                role="alert"
                data-testid="login-sso-notice"
                className="mb-6 rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-destructive"
              >
                {t(orgSsoNoticeKey)}
              </div>
            ) : null}
            {searchParams.get('reason') === 'company_disabled' && (
              <div
                role="alert"
                className="mb-6 rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-destructive"
              >
                {t('auth.companyDisabled')}
              </div>
            )}
            {/* A7: the company's own app is down; what still works is drawn below. */}
            {view ? (
              <SignInUnavailable apps={view.unavailable.map(appName)} passkeyOffered={passkeyOffered} />
            ) : null}
            {/* A1: on dash, the work e-mail named a company that changed how it signs in. */}
            {emailView && tabs.length > 0 ? (
              <div className="mb-6">
                <SignInCompanyNote company={emailView.company} />
              </div>
            ) : null}
            {view && tabs.length === 0 ? (
              // Neither password nor e-mail code: the company's providers, the passkey (A1, A4, A7).
              <div className="space-y-4" data-testid="sign-in-company-ways-in">
                {emailView ? (
                  <div className="space-y-2">
                    <Label htmlFor="company-email">{t('auth.email')}</Label>
                    <Input id="company-email" type="email" dir="ltr" readOnly value={emailLookup?.email ?? ''} />
                  </div>
                ) : null}
                {emailView ? <SignInCompanyNote company={emailView.company} /> : null}
                {view.redirects.map((button, index) => (
                  <SignInRedirectButton
                    key={button.method}
                    button={button}
                    wording={emailView ? 'continueWith' : 'signInWith'}
                    primary={index === 0}
                    loading={redirecting === button.method}
                    disabled={redirecting !== null}
                    onClick={() => void startRedirect(button, emailLookup?.email ?? null)}
                  />
                ))}
                {view.passkeyAlone === 'link' ? (
                  <button
                    type="button"
                    className="block w-full text-center text-xs text-muted-foreground hover:text-primary disabled:opacity-50"
                    disabled={isSubmitting}
                    onClick={onPasskeyLogin}
                  >
                    {t('signInPage.buttons.passkey')}
                  </button>
                ) : null}
                {view.passkeyAlone === 'button' ? (
                  <Button
                    type="button"
                    variant="outline"
                    className="w-full h-11 sm:h-10"
                    disabled={isSubmitting}
                    onClick={onPasskeyLogin}
                  >
                    {isSubmitting ? (
                      <Loader2 className="h-4 w-4 me-2 animate-spin" aria-hidden="true" />
                    ) : (
                      <Fingerprint className="h-4 w-4 me-2" aria-hidden="true" />
                    )}
                    {t('signInPage.buttons.passkey')}
                  </Button>
                ) : null}
                {renderRefusal('passkey')}
                {emailView ? (
                  <button
                    type="button"
                    className="block w-full text-center text-xs text-muted-foreground hover:text-primary"
                    onClick={changeEmail}
                  >
                    {t('signInPage.company.differentEmail')}
                  </button>
                ) : null}
              </div>
            ) : (
            <Tabs value={tabValue} onValueChange={(value) => setActiveTab(value as FormTab)} className="w-full">
              {/* mt-3 gives the floating "Last used" badge room — without it the badge
                  is clipped by the tab row.
                  Each tab shows a SHORT label (auth.methodTabs.*) at every width and
                  may wrap to a second line, never an ellipsis: at ~131px a tab cut
                  "Email + Pa…" and "OTP (Pass…" on desktop, and phones read two
                  English literals (browser QA as nine roles, 2026-10-06). The full
                  method name stays the tab's accessible name and its tooltip. */}
              <TabsList className="w-full mb-6 mt-3 h-auto items-stretch p-1">
                {tabs.includes('password') && (
                <TabsTrigger
                  value="password"
                  className={METHOD_TAB_CLASS}
                  aria-label={t('auth.emailPassword')}
                  title={t('auth.emailPassword')}
                >
                  {lastUsed === 'password' && <LastUsedBadge floating />}
                  <KeyRound className="h-3.5 w-3.5 shrink-0" />
                  <span className="min-w-0 break-words">{t('auth.methodTabs.password')}</span>
                </TabsTrigger>
                )}
                {tabs.includes('otp') && (
                <TabsTrigger
                  value="otp"
                  className={METHOD_TAB_CLASS}
                  aria-label={t('auth.otpPasswordless')}
                  title={t('auth.otpPasswordless')}
                >
                  {lastUsed === 'otp' && <LastUsedBadge floating />}
                  <Mail className="h-3.5 w-3.5 shrink-0" />
                  <span className="min-w-0 break-words">{t('auth.methodTabs.otp')}</span>
                </TabsTrigger>
                )}
                {tabs.includes('passkey') && (
                <TabsTrigger
                  value="passkey"
                  className={METHOD_TAB_CLASS}
                  aria-label={t('auth.passkey')}
                  title={t('auth.passkey')}
                >
                  {lastUsed === 'passkey' && <LastUsedBadge floating />}
                  <Fingerprint className="h-3.5 w-3.5 shrink-0" />
                  <span className="min-w-0 break-words">{t('auth.methodTabs.passkey')}</span>
                </TabsTrigger>
                )}
              </TabsList>

              {/* Tab 1: Email + Password */}
              {tabs.includes('password') && (
              <TabsContent value="password" className={TAB_PANEL_FOCUS}>
                <form onSubmit={loginForm.handleSubmit(onLoginSubmit)} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="login-email">{t('auth.email')}</Label>
                    <Input
                      id="login-email"
                      type="email"
                      placeholder={t('auth.emailPlaceholder')}
                      autoComplete="email"
                      {...loginEmailField}
                      // dash: the e-mail's domain may name a company that changed how it signs in (A1).
                      onBlur={(e) => {
                        void loginEmailField.onBlur(e)
                        lookUpEmail(e.target.value)
                      }}
                      onChange={(e) => {
                        void loginEmailField.onChange(e)
                        setRefusal(null)
                      }}
                      aria-invalid={!!loginForm.formState.errors.email}
                    />
                    {loginForm.formState.errors.email && (
                      <p className="text-sm text-destructive">
                        {loginForm.formState.errors.email.message}
                      </p>
                    )}
                  </div>

                  {renderRefusal('password')}
                  {refusal?.method === 'password' && refusal.view ? null : (
                  <>
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <Label htmlFor="login-password">{t('auth.password')}</Label>
                      <Link
                        href="/auth/forgot-password"
                        className="text-xs text-muted-foreground hover:text-primary transition-colors"
                      >
                        {t('auth.forgotPassword')}
                      </Link>
                    </div>
                    <div className="relative">
                      <Input
                        id="login-password"
                        type={showPassword ? 'text' : 'password'}
                        placeholder={t('auth.passwordPlaceholder')}
                        autoComplete="current-password"
                        className="pe-10"
                        {...loginForm.register('password')}
                        aria-invalid={!!loginForm.formState.errors.password}
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute end-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                        tabIndex={-1}
                        // axe button-name (accessibility review, 2026-10-05):
                        // an icon-only toggle says what it does and whether it is on.
                        aria-label={showPassword ? t('shell.inputs.hidePassword') : t('shell.inputs.showPassword')}
                        aria-pressed={showPassword}
                      >
                        {showPassword ? (
                          <EyeOff className="h-4 w-4" aria-hidden="true" />
                        ) : (
                          <Eye className="h-4 w-4" aria-hidden="true" />
                        )}
                      </button>
                    </div>
                    {loginForm.formState.errors.password && (
                      <p className="text-sm text-destructive">
                        {loginForm.formState.errors.password.message}
                      </p>
                    )}
                  </div>

                  <Button type="submit" className="w-full h-11 sm:h-10" disabled={isSubmitting}>
                    {isSubmitting ? (
                      <>
                        <Loader2 className="h-4 w-4 me-2 animate-spin" />
                        {t('auth.signingIn')}
                      </>
                    ) : (
                      t('auth.signIn')
                    )}
                  </Button>
                  </>
                  )}
                </form>
              </TabsContent>
              )}

              {/* Tab 2: OTP (Passwordless) */}
              {tabs.includes('otp') && (
              <TabsContent value="otp" className={TAB_PANEL_FOCUS}>
                {!otpSent ? (
                  <form onSubmit={otpEmailForm.handleSubmit(onSendOtp)} className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="otp-email">{t('auth.email')}</Label>
                      <Input
                        id="otp-email"
                        type="email"
                        placeholder={t('auth.emailPlaceholder')}
                        autoComplete="email"
                        {...otpEmailField}
                        onBlur={(e) => {
                          void otpEmailField.onBlur(e)
                          lookUpEmail(e.target.value)
                        }}
                        onChange={(e) => {
                          void otpEmailField.onChange(e)
                          setRefusal(null)
                        }}
                        aria-invalid={!!otpEmailForm.formState.errors.email}
                      />
                      {otpEmailForm.formState.errors.email && (
                        <p className="text-sm text-destructive">
                          {otpEmailForm.formState.errors.email.message}
                        </p>
                      )}
                    </div>

                    {renderRefusal('email_code')}
                    {refusal?.method === 'email_code' && refusal.view ? null : (
                    <>
                    <p className="text-xs text-muted-foreground">
                      {t('auth.otpDescription')}
                    </p>

                    <Button type="submit" className="w-full h-11 sm:h-10" disabled={isSubmitting}>
                      {isSubmitting ? (
                        <>
                          <Loader2 className="h-4 w-4 me-2 animate-spin" />
                          {t('auth.sendingOtp')}
                        </>
                      ) : (
                        t('auth.sendOtp')
                      )}
                    </Button>
                    </>
                    )}
                  </form>
                ) : (
                  <form onSubmit={otpVerifyForm.handleSubmit(onVerifyOtp)} className="space-y-4">
                    <div className="rounded-lg border border-border bg-muted/30 p-3 text-sm">
                      <p className="text-muted-foreground">
                        {t('auth.otpSentDesc', { email: otpEmail })}
                      </p>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="otp-code">{t('auth.verificationCode')}</Label>
                      <Input
                        id="otp-code"
                        type="text"
                        inputMode="numeric"
                        autoComplete="one-time-code"
                        placeholder="000000"
                        maxLength={6}
                        className="text-center text-lg tracking-[0.5em] font-mono"
                        {...otpVerifyForm.register('otp', {
                          // Show the digits that will be sent: ١٢٣ becomes 123 as typed.
                          onChange: (e) => otpVerifyForm.setValue('otp', codeDigits(e.target.value)),
                        })}
                        aria-invalid={!!otpVerifyForm.formState.errors.otp}
                      />
                      {otpVerifyForm.formState.errors.otp && (
                        <p className="text-sm text-destructive">
                          {otpVerifyForm.formState.errors.otp.message}
                        </p>
                      )}
                    </div>

                    <Button type="submit" className="w-full h-11 sm:h-10" disabled={isSubmitting}>
                      {isSubmitting ? (
                        <>
                          <Loader2 className="h-4 w-4 me-2 animate-spin" />
                          {t('auth.verifying')}
                        </>
                      ) : (
                        t('auth.verifyOtp')
                      )}
                    </Button>

                    <div className="flex items-center justify-center gap-1">
                      <span className="text-xs text-muted-foreground">{t('auth.didntReceiveCode')}</span>
                      <button
                        type="button"
                        className="text-xs text-primary hover:underline"
                        onClick={() => {
                          setOtpSent(false)
                          otpVerifyForm.reset()
                        }}
                      >
                        {t('auth.sendAgain')}
                      </button>
                    </div>
                  </form>
                )}
              </TabsContent>
              )}

              {/* Tab 3: Passkey (Biometric) */}
              {tabs.includes('passkey') && (
              <TabsContent value="passkey" className={TAB_PANEL_FOCUS}>
                <div className="space-y-4">
                  {renderRefusal('passkey')}
                  <p className="text-xs text-muted-foreground">
                    {t('auth.passkeyDescription')}
                  </p>

                  <Button
                    type="button"
                    className="w-full h-11 sm:h-10"
                    disabled={isSubmitting}
                    onClick={onPasskeyLogin}
                  >
                    {isSubmitting ? (
                      <>
                        <Loader2 className="h-4 w-4 me-2 animate-spin" />
                        {t('auth.verifying')}
                      </>
                    ) : (
                      <>
                        <Fingerprint className="h-4 w-4 me-2" />
                        {t('auth.signInWithPasskey')}
                      </>
                    )}
                  </Button>
                </div>
              </TabsContent>
              )}
            </Tabs>
            )}
            {emailView && tabs.length > 0 ? (
              <button
                type="button"
                className="mt-4 block w-full text-center text-xs text-muted-foreground hover:text-primary"
                onClick={changeEmail}
              >
                {t('signInPage.company.differentEmail')}
              </button>
            ) : null}
          </CardContent>
        </Card>

        {view ? (
          // The company's page: its providers beside its form (A3, A5), the emergency owner (A4, A7), whose accounts
          // sign in here. Never HumanFirewall's "Sign in with SSO" by e-mail: the company's own apps are its buttons.
          <>
            <div className="mt-6 space-y-3">
              {tabs.length > 0 && view.redirects.length > 0 ? (
                <>
                  <div className="relative">
                    <div className="absolute inset-0 flex items-center">
                      <span className="w-full border-t" />
                    </div>
                    <div className="relative flex justify-center text-xs uppercase">
                      <span className="bg-background px-2 text-muted-foreground">{t('auth.orContinueWith')}</span>
                    </div>
                  </div>
                  {providerRow(view.redirects)}
                </>
              ) : null}
              {view.emergency ? (
                <div className="text-center">
                  <Link href={ROUTES.signIn.emergency} className="text-xs text-muted-foreground hover:text-primary">
                    {t('signInPage.buttons.emergency')}
                  </Link>
                </div>
              ) : null}
            </div>
            {addressView ? (
              <div className="mt-6 space-y-3 text-center">
                <SignInCompanyFooter company={addressView.company} ownDomain={addressView.hostKind === 'customer_cname'} />
              </div>
            ) : null}
          </>
        ) : (
        <>
        {/* OAuth Social Login */}
        <div className="mt-6 space-y-3">
          <div className="relative">
            <div className="absolute inset-0 flex items-center">
              <span className="w-full border-t" />
            </div>
            <div className="relative flex justify-center text-xs uppercase">
              <span className="bg-background px-2 text-muted-foreground">{t('auth.orContinueWith')}</span>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Button
              variant="outline"
              type="button"
              className="h-11 sm:h-10"
              disabled={ssoLoading !== null}
              onClick={() => {
                setSsoLoading('microsoft')
                // Staged, not recorded: the operator is about to leave the page
                // and may abandon at the provider. /auth/sso/complete promotes
                // this only on a confirmed return.
                stageSsoAttempt('microsoft')
                // /auth/sso/complete finishes this sign-in, so it takes `next` from here.
                holdReturnPath(searchParams.get('next'))
                startSharedApp('microsoft').catch(() => {
                  setSsoLoading(null)
                  toast.error(t('auth.loginFailed'), { description: t('signInErrors.unknown') })
                })
              }}
            >
              {ssoLoading === 'microsoft' ? (
                <Loader2 className="me-2 h-4 w-4 animate-spin" />
              ) : (
                <svg className="me-2 h-4 w-4" viewBox="0 0 21 21" fill="none">
                  <rect x="1" y="1" width="9" height="9" fill="#F25022"/>
                  <rect x="11" y="1" width="9" height="9" fill="#7FBA00"/>
                  <rect x="1" y="11" width="9" height="9" fill="#00A4EF"/>
                  <rect x="11" y="11" width="9" height="9" fill="#FFB900"/>
                </svg>
              )}
              {t('auth.microsoft')}
              {lastUsed === 'microsoft' && <LastUsedBadge />}
            </Button>
            <Button
              variant="outline"
              type="button"
              className="h-11 sm:h-10"
              disabled={ssoLoading !== null}
              onClick={() => {
                setSsoLoading('google')
                // Staged, not recorded: the operator is about to leave the page
                // and may abandon at the provider. /auth/sso/complete promotes
                // this only on a confirmed return.
                stageSsoAttempt('google')
                // /auth/sso/complete finishes this sign-in, so it takes `next` from here.
                holdReturnPath(searchParams.get('next'))
                startSharedApp('google').catch(() => {
                  setSsoLoading(null)
                  toast.error(t('auth.loginFailed'), { description: t('signInErrors.unknown') })
                })
              }}
            >
              {ssoLoading === 'google' ? (
                <Loader2 className="me-2 h-4 w-4 animate-spin" />
              ) : (
                <svg className="me-2 h-4 w-4" viewBox="0 0 24 24">
                  <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/>
                  <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                  <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
                  <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
                </svg>
              )}
              {t('auth.google')}
              {lastUsed === 'google' && <LastUsedBadge />}
            </Button>
          </div>

          {/* The organisation's own identity provider (Ping, Entra ID, Okta ...): identity integrations wave 2. */}
          {!orgSsoOpen ? (
            <Button
              variant="outline"
              type="button"
              className="h-11 w-full sm:h-10"
              data-testid="login-sso-open"
              onClick={() => setOrgSsoOpen(true)}
            >
              <Building2 className="me-2 h-4 w-4" aria-hidden="true" />
              {t('identitySso.adminSignIn.button')}
            </Button>
          ) : (
            <Card data-testid="login-sso-panel">
              <CardHeader className="pb-3">
                <CardTitle className="text-base">{t('identitySso.adminSignIn.title')}</CardTitle>
                <CardDescription>{t('identitySso.adminSignIn.description')}</CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={onOrgSsoSubmit} className="space-y-3" noValidate>
                  <div className="space-y-2">
                    <Label htmlFor="sso-email">{t('identitySso.adminSignIn.emailLabel')}</Label>
                    <Input
                      id="sso-email"
                      type="email"
                      dir="ltr"
                      autoComplete="email"
                      placeholder={t('auth.emailPlaceholder')}
                      value={orgSsoEmail}
                      onChange={(e) => setOrgSsoEmail(e.target.value)}
                      aria-invalid={orgSsoEmailInvalid}
                    />
                    {orgSsoEmailInvalid ? (
                      <p className="text-sm text-destructive">{t('identitySso.adminSignIn.emailInvalid')}</p>
                    ) : null}
                  </div>
                  <Button type="submit" className="h-11 w-full sm:h-10" disabled={orgSsoSubmitting}>
                    {orgSsoSubmitting ? (
                      <>
                        <Loader2 className="me-2 h-4 w-4 animate-spin" aria-hidden="true" />
                        {t('identitySso.adminSignIn.redirecting')}
                      </>
                    ) : (
                      <>
                        <Building2 className="me-2 h-4 w-4" aria-hidden="true" />
                        {t('identitySso.adminSignIn.continue')}
                      </>
                    )}
                  </Button>
                  <button
                    type="button"
                    className="w-full text-center text-xs text-muted-foreground hover:text-primary"
                    onClick={() => setOrgSsoOpen(false)}
                  >
                    {t('identitySso.adminSignIn.back')}
                  </button>
                </form>
              </CardContent>
            </Card>
          )}
        </div>

        {/* Footer Links */}
        <div className="mt-6 space-y-3 text-center">
          <p className="text-sm text-muted-foreground">{t('auth.noAccountContactAdmin')}</p>
        </div>
        </>
        )}
        </>
        )}
      </div>
    </div>
  )
}

// Wrap in Suspense: LoginPageInner calls useSearchParams (to read `next`),
// which Next 16 requires to be inside a Suspense boundary. The fallback
// mirrors the auth-gate loader so there's no UI flash on the boundary.
/** The last-used method never changes while the login page is mounted. */
const NO_STORE_SUBSCRIBE = () => () => {}

/**
 * A sign-in method tab: an equal share of the row, the label centred and
 * allowed to wrap (whitespace-normal overrides TabsTrigger's nowrap), every
 * tab as tall as the tallest (h-auto in a stretched row).
 */
const METHOD_TAB_CLASS =
  'h-auto min-h-8 min-w-0 flex-1 gap-1 px-1.5 py-1.5 text-center text-xs leading-tight whitespace-normal sm:gap-1.5 sm:px-2 sm:text-sm'

/**
 * "Last used" marker, built on the shared <Badge variant="outline">.
 *
 * Soft blue rather than a solid fill: it sits inside a control the operator is
 * meant to click, and a solid block outweighs the primary Sign in button.
 *
 * `floating` is for the TABS. A tab is ~131px and its short label already
 * fills it, so an inline badge would push it onto more lines. Absolutely
 * positioned, it costs the label no width at all — TabsTrigger is already
 * `relative`, and the tab row carries `mt-3` so this is not clipped.
 * The SSO buttons are ~194px, so those stay inline. Its words are the
 * catalogue's (auth.methodTabs.lastUsed): it was English on every page.
 */
function LastUsedBadge({ floating = false }: { floating?: boolean }) {
  const { t } = useTranslation()
  return (
    <Badge
      variant="outline"
      className={cn(
        'h-[15px] border-blue-200 bg-blue-50 px-1.5 text-[8.5px] leading-none text-blue-700',
        'dark:border-blue-400/45 dark:bg-blue-600/20 dark:text-blue-300',
        floating &&
          'absolute -top-2 left-1/2 z-10 -translate-x-1/2 ring-[3px] ring-background',
      )}
    >
      {t('auth.methodTabs.lastUsed')}
    </Badge>
  )
}

/**
 * A tab panel is a tab stop of its own (Radix gives it tabIndex 0), so it shows
 * the focus ring every other control here shows (accessibility review,
 * 2026-10-05: the Email + Password panel took focus with no visible ring).
 */
const TAB_PANEL_FOCUS =
  'rounded-md focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background'

export default function LoginPage() {
  return (
    <Suspense fallback={<BrandedLoader />}>
      <LoginPageInner />
    </Suspense>
  )
}
